import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Check, Trash2, Calendar, X, Bell, ChevronDown, ChevronUp, Edit2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_STATUSES = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-gray-500', icon: '⏳' },
  { value: 'en_progreso', label: 'En progreso', color: 'bg-blue-500', icon: '🔄' },
  { value: 'pausada', label: 'Pausada', color: 'bg-yellow-500', icon: '⏸️' },
  { value: 'completada', label: 'Completada', color: 'bg-green-500', icon: '✅' },
];

const STATUS_ORDER = ['pendiente', 'en_progreso', 'pausada', 'completada'];

const getPriorityColor = (priority) => {
  const colors = { alta: 'bg-red-500', media: 'bg-yellow-500', baja: 'bg-green-500' };
  return colors[priority] || 'bg-gray-500';
};

const getStatusData = (status) => {
  return TASK_STATUSES.find(s => s.value === status) || TASK_STATUSES[0];
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'completada') return false;
  return new Date(dueDate) < new Date();
};

const emptyForm = {
  title: '',
  description: '',
  due_date: '',
  priority: 'media',
  progress: 0,
  task_status: 'pendiente',
  reminder_interval: '',
  reminder_unit: 'dias',
};

export const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadTasks(); }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data);
    } catch {
      toast.error('Error al cargar tareas');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        progress: parseInt(formData.progress) || 0,
        reminder_interval: formData.reminder_interval ? parseInt(formData.reminder_interval) : null,
      };
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.task_id}`, payload);
        toast.success('Tarea actualizada');
      } else {
        await axios.post(`${API}/tasks`, payload);
        toast.success('Tarea creada');
      }
      setFormData(emptyForm);
      setShowForm(false);
      setEditingTask(null);
      loadTasks();
    } catch {
      toast.error('Error al guardar tarea');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const newProgress = newStatus === 'completada' ? 100 : newStatus === 'en_progreso' ? 50 : newStatus === 'pausada' ? 25 : 0;
      await axios.put(`${API}/tasks/${taskId}`, {
        task_status: newStatus,
        status: newStatus === 'completada' ? 'completed' : 'pending',
        progress: newProgress,
      });
      loadTasks();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleProgressChange = async (taskId, progress) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { progress: parseInt(progress) });
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, progress: parseInt(progress) } : t));
    } catch {
      toast.error('Error al actualizar progreso');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Tarea eliminada');
      if (selectedTask?.task_id === taskId) setSelectedTask(null);
      loadTasks();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority || 'media',
      progress: task.progress || 0,
      task_status: task.task_status || 'pendiente',
      reminder_interval: task.reminder_interval || '',
      reminder_unit: task.reminder_unit || 'dias',
    });
    setShowForm(true);
  };

  const filteredTasks = tasks.filter(t => {
    if (filterStatus !== 'all' && (t.task_status || 'pendiente') !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    return true;
  });

  const counts = {
    pendiente: tasks.filter(t => (t.task_status || 'pendiente') === 'pendiente').length,
    en_progreso: tasks.filter(t => t.task_status === 'en_progreso').length,
    completada: tasks.filter(t => t.task_status === 'completada').length,
    total: tasks.length,
  };

  const stagger = { show: { transition: { staggerChildren: 0.06 } } };
  const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } } };

  const statCards = [
    { label: 'Pendientes', count: counts.pendiente, accent: '#c9a84c', icon: '⏳' },
    { label: 'En progreso', count: counts.en_progreso, accent: '#00d4c8', icon: '🔄' },
    { label: 'Completadas', count: counts.completada, accent: '#34d399', icon: '✓' },
    { label: 'Total', count: counts.total, accent: '#a78bfa', icon: '#' },
  ];

  return (
    <div className="min-h-screen bg-[#050508] p-4 md:p-6">
      <div className="max-w-5xl mx-auto pt-20 md:pt-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="mb-8">
          <PageTitle>Mis Tareas</PageTitle>
          <p className="text-white/40 text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>Gestiona progreso, estados y recordatorios</p>
        </motion.div>

        {/* Stats */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {statCards.map(s => (
            <motion.div key={s.label} variants={fadeUp}
              className="bg-[#0d0d14] border border-white/7 rounded-xl p-4 relative overflow-hidden group"
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle at 50% 0%, ${s.accent}18, transparent 70%)` }} />
              <div className="text-white/40 text-xs font-medium mb-2 tracking-widest uppercase" style={{ fontFamily: 'var(--font-body)' }}>{s.label}</div>
              <div className="text-3xl font-bold" style={{ color: s.accent, fontFamily: 'var(--font-display)' }}>{s.count}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-2 mb-5">
          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => { setEditingTask(null); setFormData(emptyForm); setShowForm(!showForm); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', fontFamily: 'var(--font-body)' }}
          >
            <Plus size={16} />
            Nueva Tarea
          </motion.button>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border"
            style={{ background: '#0d0d14', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.07)', fontFamily: 'var(--font-body)' }}
          >
            <option value="all">Todos los estados</option>
            {TASK_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border"
            style={{ background: '#0d0d14', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.07)', fontFamily: 'var(--font-body)' }}
          >
            <option value="all">Todas las prioridades</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </div>

        {/* Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.3 }}
              className="rounded-xl p-5 mb-5 border"
              style={{ background: '#0d0d14', borderColor: 'rgba(201,168,76,0.2)' }}
            >
              <h3 className="text-sm font-semibold mb-4" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
                {editingTask ? 'EDITAR TAREA' : 'NUEVA TAREA'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">TÍTULO *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                      required
                      placeholder="Ej: Llamar al cliente García"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">FECHA LÍMITE</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">DESCRIPCIÓN</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border h-16 resize-none"
                    style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    placeholder="Detalles de la tarea..."
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">PRIORIDAD</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">ESTADO</label>
                    <select
                      value={formData.task_status}
                      onChange={(e) => setFormData({ ...formData, task_status: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      {TASK_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-medium mb-1 block tracking-widest">PROGRESO: {formData.progress}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={formData.progress}
                      onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                      className="w-full mt-2"
                      style={{ accentColor: '#c9a84c' }}
                    />
                  </div>
                </div>

                {/* Recordatorio */}
                <div className="rounded-lg p-3 border" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)' }}>
                  <label className="text-white/50 text-xs font-medium mb-2 flex items-center gap-2 tracking-widest">
                    <Bell size={12} style={{ color: '#c9a84c' }} />
                    RECORDATORIO (opcional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="Cada..."
                      value={formData.reminder_interval}
                      onChange={(e) => setFormData({ ...formData, reminder_interval: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    />
                    <select
                      value={formData.reminder_unit}
                      onChange={(e) => setFormData({ ...formData, reminder_unit: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg text-sm border"
                      style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <option value="horas">Horas</option>
                      <option value="dias">Días</option>
                      <option value="semanas">Semanas</option>
                      <option value="meses">Meses</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button type="submit"
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-opacity hover:opacity-80"
                    style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}
                  >
                    {editingTask ? 'Guardar cambios' : 'Crear Tarea'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingTask(null); setFormData(emptyForm); }}
                    className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-opacity hover:opacity-70"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#7a7870', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tasks List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }} />
          </div>
        ) : filteredTasks.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16 rounded-xl border"
            style={{ background: '#0d0d14', borderColor: 'rgba(255,255,255,0.07)' }}>
            <Calendar size={40} className="mx-auto mb-4 opacity-20" style={{ color: '#c9a84c' }} />
            <p className="text-white/30 text-sm" style={{ fontFamily: 'var(--font-body)' }}>No hay tareas que mostrar</p>
          </motion.div>
        ) : (
          <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-2">
            {filteredTasks.map(task => {
              const statusData = getStatusData(task.task_status || 'pendiente');
              const progress = task.progress || 0;
              const overdue = isOverdue(task.due_date, task.task_status);
              const priorityColors = { alta: '#ef4444', media: '#f59e0b', baja: '#34d399' };
              const priorityColor = priorityColors[task.priority] || '#7a7870';

              return (
                <motion.div
                  key={task.task_id}
                  variants={fadeUp}
                  layout
                  className="rounded-xl p-4 border transition-all group"
                  style={{
                    background: '#0d0d14',
                    borderColor: task.task_status === 'completada' ? 'rgba(52,211,153,0.2)' :
                      overdue ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.07)',
                    opacity: task.task_status === 'completada' ? 0.6 : 1,
                  }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold mb-0.5" style={{
                        color: task.task_status === 'completada' ? '#7a7870' : '#e8e6e0',
                        textDecoration: task.task_status === 'completada' ? 'line-through' : 'none',
                        fontFamily: 'var(--font-body)'
                      }}>
                        {task.title}
                      </h3>
                      {task.description && (
                        <p className="text-xs line-clamp-2" style={{ color: '#7a7870' }}>{task.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: `${priorityColor}18`, color: priorityColor, border: `1px solid ${priorityColor}30` }}>
                        {task.priority}
                      </span>
                      <button onClick={() => openEdit(task)} className="p-1 rounded transition-colors hover:text-white" style={{ color: '#7a7870' }}>
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDelete(task.task_id)} className="p-1 rounded transition-colors" style={{ color: '#7a7870' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                        onMouseLeave={e => e.currentTarget.style.color = '#7a7870'}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs" style={{ color: '#7a7870' }}>Progreso</span>
                      <span className="text-xs font-medium" style={{ color: progress === 100 ? '#34d399' : '#c9a84c' }}>{progress}%</span>
                    </div>
                    <div className="w-full rounded-full h-1.5 relative" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-1.5 rounded-full transition-all"
                        style={{ width: `${progress}%`, background: progress === 100 ? '#34d399' : 'linear-gradient(90deg, #c9a84c, #00d4c8)' }} />
                    </div>
                    <input
                      type="range" min="0" max="100" step="5"
                      value={progress}
                      onChange={(e) => handleProgressChange(task.task_id, e.target.value)}
                      className="w-full mt-1 h-1 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ accentColor: '#c9a84c' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Status Pipeline */}
                  <div className="flex items-center gap-1 mb-3 overflow-x-auto">
                    {TASK_STATUSES.map((s, idx) => {
                      const currentIdx = STATUS_ORDER.indexOf(task.task_status || 'pendiente');
                      const isActive = s.value === (task.task_status || 'pendiente');
                      const isPast = idx < currentIdx;
                      return (
                        <React.Fragment key={s.value}>
                          <button
                            onClick={() => handleStatusChange(task.task_id, s.value)}
                            className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap transition-all"
                            style={{
                              background: isActive ? 'rgba(201,168,76,0.2)' : isPast ? 'rgba(52,211,153,0.1)' : 'rgba(255,255,255,0.04)',
                              color: isActive ? '#c9a84c' : isPast ? '#34d399' : '#7a7870',
                              border: `1px solid ${isActive ? 'rgba(201,168,76,0.4)' : isPast ? 'rgba(52,211,153,0.3)' : 'rgba(255,255,255,0.06)'}`,
                              fontFamily: 'var(--font-body)'
                            }}
                          >
                            {s.icon} {s.label}
                          </button>
                          {idx < TASK_STATUSES.length - 1 && (
                            <div className="h-px flex-1 min-w-2" style={{ background: isPast || isActive ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.06)' }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center gap-4 text-xs" style={{ color: '#7a7870' }}>
                    {task.due_date && (
                      <span className="flex items-center gap-1" style={{ color: overdue ? '#ef4444' : '#7a7870' }}>
                        <Calendar size={11} />
                        {new Date(task.due_date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {overdue && ' · Vencida'}
                      </span>
                    )}
                    {task.reminder_interval && (
                      <span className="flex items-center gap-1" style={{ color: '#00d4c8' }}>
                        <Bell size={11} />
                        Cada {task.reminder_interval} {task.reminder_unit}
                      </span>
                    )}
                  </div>

                  {task.pipeline_next_estado && task.task_status !== 'completada' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStatusChange(task.task_id, 'completada'); }}
                      className="mt-3 w-full py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all"
                      style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.1)'}
                    >
                      ✓ Completar y avanzar a <span className="uppercase tracking-wide ml-1">{task.pipeline_next_estado}</span> →
                    </button>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};
